import { Component, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { decrypt, getExportReportBugId } from '@piqet-angular/data';
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { ReportsService } from 'libs/data/src/lib/services/report/reports.service';

import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { CookieService } from 'ngx-cookie-service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { CustomModalService, loaderMessage } from '@piqet-angular/shared';
import { ToastrService } from 'ngx-toastr';
import { of, Subscription } from 'rxjs';
import * as saveAs from 'file-saver';
import { formatDate } from '@angular/common';
import {
  ReportExportSignalrService,
  ExportConnectionFailedError,
  ReportHubMethod,
} from 'libs/data/src/lib/services/report/report-export-signalr.service';

@Component({
  selector: 'piqet-angular-project-detail-report',
  templateUrl: './project-detail-report.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./project-detail-report.component.scss'],
})
export class ProjectDetailReportComponent implements OnDestroy {
  reportData: any;
  replicaReportData: any;
  responceIsExportData: any;
  exportReportData: any;
  activatedRoute: any;
  exportPayload: any;
  display = false;
  projectId: any;
  projectType!: string;
  @ViewChild('pdf', { static: false }) pdf!: PDFExportComponent;
  @ViewChild('pdfExportData', { static: false })
  pdfExportData!: PDFExportComponent;
  fileType!: string;
  payload: any;
  userList: any;
  // Latest status text streamed from the SignalR hub. Bound to <piqet-angular-report-progress>
  // and shown in place of the report while it is being generated.
  progressMessage = '';
  private reportSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private modal: CustomModalService,
    private reportService: ReportsService,
    private swalService: SwalService,
    private sharedService: SharedService,
    private cookieService: CookieService,
    private toaster: ToastrService,
    private reportExportSignalr: ReportExportSignalrService
  ) {}
  ngOnDestroy(): void {
    this.reportSub?.unsubscribe();
    this.progressMessage = '';
  }

  ngOnInit() {
    this.activatedRoute = this.route.snapshot.routeConfig?.path;

    this.route.queryParams.subscribe((params: any) => {
      const decryptedPayload = decrypt(decodeURIComponent(params.payload));

      this.payload = JSON.parse(decryptedPayload);

      this.sharedService.scenarioNameFunc('Report generation');
    });
    this.reportAPI();
  }

  async reportAPI() {
    // Seed the in-place progress component so something is visible before the first
    // SignalR ReportProgress event arrives.
    this.progressMessage = this.payload?.loaderMessage || 'Starting…';
    switch (this.payload.reportType) {
      case 3:
        const response = await this.runReportJob('StartFullDetailedReport', this.payload, () =>
          this.reportService.generateFullReport(this.payload)
        );
        this.progressMessage = '';
        if (response.messageType === 0) {
          response.data.flag = this.payload.isCheckeportListByTypeByFlag;
          response.data.isCheckeportListByTypeById = this.payload.isCheckeportListByTypeById;
          response.data.isCheckeportListByFormateById = this.payload.isCheckeportListByFormateById;
          this.reportData = response.data;
          this.replicaReportData = response.data;
          this.exportPayload = this.payload;
          this.projectId = this.reportData.projectId;
          this.getBreadCrumbName();
        } else {
          this.errorGenerateReport(this.payload, response);
        }
        break;
      case 5:
        try {
          const responsePdf = await this.runReportJob('StartExportCsv', this.payload, () =>
            this.reportService.getExcelSummaryLink(this.payload)
          );
          if (responsePdf.messageType === 0) {
            const downloadLink = document.createElement('a');
            downloadLink.href = responsePdf.data;
            downloadLink.download = 'report.pdf'; // The name of the downloaded PDF file
            downloadLink.click();
            setTimeout(() => {
              window.close();
            }, 1000);
          } else {
            this.errorGenerateReport(this.payload, responsePdf);
          }
        } catch (error) {
          this.toaster.error('Error downloading the file. Please try again.');
        }
        break;
      case 4:
        const responseCompact = await this.runReportJob('StartPiqetCompactReport', this.payload, () =>
          this.reportService.generateCompactReport(this.payload)
        );
        this.progressMessage = '';
        this.payload.isExport = true;
        if (responseCompact.messageType === 0) {
          responseCompact.data.flag = this.payload.isCheckeportListByTypeByFlag;
          responseCompact.data.isCheckeportListByTypeById = this.payload.isCheckeportListByTypeById;
          responseCompact.data.isCheckeportListByFormateById =
            this.payload.isCheckeportListByFormateById;
          this.reportData = responseCompact.data;
          this.replicaReportData = responseCompact.data;
          this.exportPayload = this.payload;
          this.projectId = this.reportData.projectId;
          this.getBreadCrumbName();
        } else {
          {
            this.errorGenerateReport(this.payload, responseCompact);
          }
        }
        break;
      case 6:
        const responseProductWaste = await this.reportService.generateProductWasteReport(
          this.payload
        );
        this.progressMessage = '';
        if (responseProductWaste.messageType === 0) {
          responseProductWaste.data.flag = this.payload.isCheckeportListByTypeByFlag;
          responseProductWaste.data.isCheckeportListByTypeById =
            this.payload.isCheckeportListByTypeById;
          responseProductWaste.data.isCheckeportListByFormateById =
            this.payload.isCheckeportListByFormateById;
          this.reportData = responseProductWaste.data;
          this.replicaReportData = responseProductWaste.data;
          this.exportPayload = this.payload;
          this.projectId = this.reportData.projectId;
          this.getBreadCrumbName();
        } else {
          this.errorGenerateReport(this.payload, responseProductWaste);
        }
        break;
      case 8:
        const responseCarbon = await this.reportService.generateCarbonFootprintReport(this.payload);
        this.progressMessage = '';
        if (responseCarbon.messageType === 0) {
          responseCarbon.data.flag = this.payload.isCheckeportListByTypeByFlag;
          responseCarbon.data.isCheckeportListByTypeById = this.payload.isCheckeportListByTypeById;
          responseCarbon.data.isCheckeportListByFormateById =
            this.payload.isCheckeportListByFormateById;
          this.reportData = responseCarbon.data;
          this.replicaReportData = responseCarbon.data;
          this.exportPayload = this.payload;
          this.projectId = this.reportData.projectId;
          this.getBreadCrumbName();
        } else {
          this.errorGenerateReport(this.payload, responseCarbon);
        }
        break;
      case 11: // ISO  Report
      case 0: // custom   Report
        const resISOReport = await this.runReportJob('StartFullISOReport', this.payload, () =>
          this.reportService.generateISOReport(this.payload)
        );
        this.progressMessage = '';
        if (resISOReport.messageType === 0) {
          if (this.payload.reportTypeIdByList == 0) {
            resISOReport.data.reportFormatId = 0;
            resISOReport.data.flag = this.payload.flag;
            resISOReport.data.isCheckeportListByTypeById = this.payload.isCheckeportListByTypeById;
            resISOReport.data.isCheckeportListByFormateById =
              this.payload.isCheckeportListByFormateById;
          }
          this.reportData = resISOReport.data;
          this.replicaReportData = resISOReport.data;
          this.exportPayload = this.payload;
          this.projectId = this.reportData.projectId;
          this.getBreadCrumbName();
        } else {
          this.errorGenerateReport(this.payload, resISOReport);
        }

        break;
      default:
        break;
    }
    if (this.reportData) {
      this.reportData.reportGenerateDate = Date.now();
    }
  }

  // Runs a report over SignalR and resolves with the same { messageType, data }
  // shape the existing POST returned, so all rendering below stays unchanged.
  // Progress events drive the loader text; a connection failure falls back to the
  // original HTTP call, while a server failure/timeout resolves a non-zero
  // messageType so the existing error path handles it.
  private runReportJob(
    hubMethod: ReportHubMethod,
    payload: any,
    httpFallback: () => Promise<any>
  ): Promise<any> {
    return new Promise(resolve => {
      let resolved = false;
      const done = (value: any) => {
        if (resolved) return;
        resolved = true;
        resolve(value);
      };

      this.reportSub = this.reportExportSignalr.generateReport(hubMethod, payload).subscribe({
        next: ev => {
          if (ev.kind === 'progress') {
            this.progressMessage = ev.message || payload.loaderMessage || 'Working…';
          } else {
            done({
              messageType: ev.result.messageType,
              data: ev.result.data ? JSON.parse(ev.result.data) : null,
              message: ev.result.message,
            });
          }
        },
        error: err => {
          if (err instanceof ExportConnectionFailedError) {
            httpFallback()
              .then(done)
              .catch(() => done({ messageType: 1, message: 'Failed to generate report' }));
          } else {
            done({ messageType: 1, message: (err && err.message) || 'Failed to generate report' });
          }
        },
      });
    });
  }

  getBreadCrumbName() {
    switch (this.reportData?.reportFormatId) {
      // iso report
      case 11:
        this.projectType = 'ISO report';
        break;
      //Full report
      case 3:
        this.projectType = 'Project detailed report';
        break;
      // compact report
      case 4:
        this.projectType = 'Compact report';
        break;
      case 0:
        this.projectType = 'Custom report';
        break;
      default:
        this.projectType = 'Project detailed report';
        break;
    }
  }

  downloadPDF(payload: any, generateURL: any, type: any) {
    const newPayload = { ...payload, userList: this.userList };
    this.reportService.directGenertePDF(newPayload, generateURL).subscribe(
      blob => {
        this.reportService.processMailReportResponse(blob, {
          payload: newPayload,
          onError: (payload, error) => this.errorGenerateReport(payload, error),
          onMailSuccess: () => {
            if (newPayload.isMail) {
              this.toaster.success('Your report will be shared via your email shortly');
              this.modal.close();
            } else {
              const cleanType = type && type.trim() ? `_${type.trim().replace(/\s+/g, '_')}` : '';
              let fileName = `${newPayload.reportName}${cleanType}.pdf`;
              if (fileName?.includes('.')) {
                fileName = fileName.split('.')[0];
              }
              saveAs(blob, fileName);
            }
          },
        });
      },
      error => {
        this.errorGenerateReport(newPayload, error);
      }
    );
  }

  errorGenerateReport(payload: any, response: any) {
    this.swalService
      .confirm(
        'Report this issue',
        'An error occurred while generating your report. Do you want to report this issue to the Piqet team?'
      )
      .then(confirmed => {
        if (confirmed) {
          this.openReportBugModal(payload, response);
        }
      });
  }

  openReportBugModal(payload: any, response: any) {
    this.modal.invoke('reportBug', {
      initialState: {
        payload,
        response,
        categoryInfo: getExportReportBugId(),
      },
      class: 'modal-xl',
    });
  }

  async pdfExport(isMail: boolean) {
    if (isMail) {
      this.exportPayload.isMail = true;
      this.exportPayload.isExport = true;
    } else {
      this.exportPayload.isExport = true;
      this.exportPayload.isMail = false;
    }
    this.sharedService.show(loaderMessage);
    this.exportPayload['reportGeneratedDate'] = formatDate(
      this.reportData.reportGenerateDate,
      'yyyy-MM-dd HH:mm',
      'en-US'
    );
    switch (this.reportData.reportFormatId) {
      case 0:
        this.fileType = 'CustomReport.pdf';
        this.exportPayload['APIToken'] = this.cookieService.get('token');
        this.exportPayload.indicatorId =
          this.exportPayload.indicatorId == '' ? 0 : this.exportPayload.indicatorId;
        this.downloadPDF(this.exportPayload, 'PDFGenerator/CustomReport?reuqestString', null);
        break;
      case 3:
        this.fileType = 'FullReport.pdf';
        this.exportPayload['APIToken'] = this.cookieService.get('token');
        this.exportPayload.indicatorId =
          this.exportPayload.indicatorId == '' ? 0 : this.exportPayload.indicatorId;
        this.downloadPDF(this.exportPayload, 'PDFGenerator/FullReport?reuqestString', null);
        break;
      case 4:
        this.fileType = 'CompactReport.pdf';
        this.exportPayload['APIToken'] = this.cookieService.get('token');
        this.exportPayload.indicatorId =
          this.exportPayload.indicatorId == '' ? 0 : this.exportPayload.indicatorId;
        this.downloadPDF(
          this.exportPayload,
          'PDFGenerator/CompactReport?requestString',
          'Compact Report'
        );
        break;
      case 11:
        this.fileType = 'ISOReport.pdf';
        this.exportPayload['APIToken'] = this.cookieService.get('token');
        this.exportPayload.indicatorId =
          this.exportPayload.indicatorId == '' ? 0 : this.exportPayload.indicatorId;
        this.downloadPDF(
          this.exportPayload,
          'PDFGenerator/ISOReport?reuqestString',
          'ISO14044 Compliant Report'
        );
        break;
      default:
        break;
    }
  }

  exportPDF(projectName: any) {
    this.pdf?.saveAs(projectName.projectName);
    setTimeout(() => {
      this.reportData = this.replicaReportData;
    }, 4000);
  }

  openShareModal() {
    this.modal.invoke('ReportShare', {
      class: 'modal-lg',
    });
    this.modal.ref?.content.exportOrShareReport.subscribe((exportOrShareReport: any) => {
      return of((this.userList = exportOrShareReport.payload), this.pdfExport(true));
    });
  }
}
